import React from 'react';
import PropTypes from 'prop-types';
import { IconAnomalyDetection, IconGoodCondition } from 'svgIcons/MotionPortalIcons';
import IconRightArrow from 'svgIcons/MotionPortalIcons/IconRightArrow';
import './AnomalyDetection.scss';
import { useTranslation } from 'react-i18next';
import { translate as translateHelper } from 'helpers/translateHelper';
import { ACS1000_COOLING_ANOMALY_KEY } from '../../PowertrainAssetDetails/DetailedInformation/ConditionInsights/constants';


const getAnomalyDescription = (level, kpiKey, translate) => {
    if (!level) { return ''; }
    if (kpiKey === ACS1000_COOLING_ANOMALY_KEY) {
        if (level >= 4) { return translate('ABB.Powertrain.Frontend.coolingAnomalySevereDescription'); }
        if (level === 3) { return translate('ABB.Powertrain.Frontend.coolingAnomalyModerateDescription'); }
        return translate('ABB.Powertrain.Frontend.coolingAnomalyMinorDescription');
    }
    return level <= 3
        ? translate('ABB.Powertrain.Frontend.coolingAnomalyMinorDescription')
        : translate('ABB.Powertrain.Frontend.coolingAnomalySevereDescription');
};

const getAcs1000SeverityText = (level, translate) => {
    if (level >= 4) { return translate('ABB.Powertrain.Frontend.summaryAcs1000CoolingAnomalySevereText'); }
    if (level === 3) { return translate('ABB.Powertrain.Frontend.summaryAcs1000CoolingAnomalyModerateText'); }
    return translate('ABB.Powertrain.Frontend.summaryAcs1000CoolingAnomalyMinorText');
};

const getAcs1000ShowsVerb = (signalCount, translate) => signalCount > 1
    ? translate('ABB.Powertrain.Frontend.summaryAcs1000CoolingAnomalyShowsPlural')
    : translate('ABB.Powertrain.Frontend.summaryAcs1000CoolingAnomalyShowsSingular');


const renderBoldSignalNames = (names, andWord) => {
    const filtered = (names || []).filter(Boolean);
    if (!filtered.length) { return null; }
    if (filtered.length === 1) { return <strong>{filtered[0]}</strong>; }
    if (filtered.length === 2) {
        return <>
            <strong>{filtered[0]}</strong> {andWord} <strong>{filtered[1]}</strong>
        </>;
    }
    const lastIndex = filtered.length - 1;
    return <>
        {filtered.slice(0, lastIndex).map((name, index) =>
            <React.Fragment key={`${name}-${index}`}>
                <strong>{name}</strong>{', '}
            </React.Fragment>
        )}
        {andWord} <strong>{filtered[lastIndex]}</strong>
    </>;
};

const buildAnomalyRows = (activeKPIs) => {
    const handledAcs1000Levels = new Set();
    const rows = [];

    activeKPIs.forEach(kpi => {
        if (kpi?.key !== ACS1000_COOLING_ANOMALY_KEY) {
            rows.push({
                rowKey: kpi?.key,
                nameTextKey: kpi?.nameTextKey,
                name: kpi?.name,
                anomalyLevel: kpi?.anomalyLevel,
                kpiKey: kpi?.key,
                signalNames: [],
                onClickKPI: kpi,
            });
            return;
        }

        const level = kpi?.anomalyLevel;
        if (handledAcs1000Levels.has(level)) { return; }
        handledAcs1000Levels.add(level);

        const signalNames = activeKPIs
            .filter(item => item?.key === ACS1000_COOLING_ANOMALY_KEY && item?.anomalyLevel === level)
            .map(item => translateHelper(item?.timeseriesNameTextKey) || item?.timeseriesName)
            .filter(Boolean);

        rows.push({
            rowKey: `${ACS1000_COOLING_ANOMALY_KEY}-${level}`,
            nameTextKey: kpi?.nameTextKey,
            name: kpi?.name,
            anomalyLevel: level,
            kpiKey: ACS1000_COOLING_ANOMALY_KEY,
            signalNames,
            onClickKPI: kpi,
        });
    });

    return rows;
};

const AnomalyDetection = ({ anomalyKPIs, maxLevel, onClick }) => {
    const { t: translate } = useTranslation();

    const activeKPIs = (anomalyKPIs || []).filter(kpi => kpi?.anomalyLevel > 0);

    if (!activeKPIs.length) {
        return (
            <div className='render-no__anomaly-detected'>
                <IconGoodCondition />
                <p>{translate('ABB.Powertrain.Frontend.anomalyDetectEmptyLabel')}</p>
            </div>
        );
    }

    const anomalyRows = buildAnomalyRows(activeKPIs);

    return (
        <>
            {anomalyRows.map(row => {
                const rowTitle = translateHelper(row.nameTextKey) || row.name;
                const isAcs1000RowWithSignals = row.kpiKey === ACS1000_COOLING_ANOMALY_KEY && row.signalNames.length > 0;
                const description = isAcs1000RowWithSignals
                    ? <>
                        {renderBoldSignalNames(row.signalNames, translate('ABB.Powertrain.Frontend.acs1000CoolingAnomalySignalsAnd'))}
                        {' '}{getAcs1000ShowsVerb(row.signalNames.length, translate)}{' '}
                        {getAcs1000SeverityText(row.anomalyLevel, translate)}
                    </>
                    : getAnomalyDescription(row.anomalyLevel, row.kpiKey, translate);

                return <button className='anomaly-detection-row w-100' key={row.rowKey} onClick={onClick ? () => onClick(row.onClickKPI) : undefined}>
                    <div className='anomaly-detection-info'>
                        <span className='anomaly-detection-icon'>
                            <IconAnomalyDetection height={18} width={18} viewBox={'0 0 18 18'} />
                        </span>
                        <div className='anomaly-detection-title'>
                            {rowTitle}
                            <span className='anomaly-detection-level'>
                                {` (${translate('ABB.Powertrain.Frontend.levelLabel')}: ${row.anomalyLevel}/${maxLevel ?? 5})`}
                            </span>
                        </div>
                    </div>
                    <div>
                        <p className='anomaly-detection-description text-start'>
                            {description}
                        </p>
                    </div>
                    <button
                        type='button'
                        className='anomaly-detection-arrow'
                        id='anomaly-detection-arrow'
                        aria-label={rowTitle}
                    >
                        <IconRightArrow width={16} height={16} color='var(--Foreground-primary, #1F1F1F)' />
                    </button>
                </button>;
            })}
        </>
    );
};

AnomalyDetection.propTypes = {
    anomalyKPIs: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        name: PropTypes.string,
        nameTextKey: PropTypes.string,
        anomalyLevel: PropTypes.number,
        condition: PropTypes.string,
        timeseriesName: PropTypes.string,
        timeseriesNameTextKey: PropTypes.string,
    })),
    maxLevel: PropTypes.number,
    onClick: PropTypes.func,
};

export default AnomalyDetection;

